import * as Sentry from '@sentry/react';
import { sentryBeforeSend } from './sentryBeforeSend';
import { scrubUrlPath } from './sentryContextSanitizer';
import {
  getSentryDsn,
  getSentryEnvironment,
  getSentryRelease,
  isSentryRuntimeEnabled,
  SENTRY_APPLICATION,
} from './sentryConfig';

const IGNORE_ERRORS = [
  'ResizeObserver loop',
  'ResizeObserver loop limit exceeded',
  'Non-Error promise rejection captured',
  /Loading chunk [\d]+ failed/i,
  /ChunkLoadError/i,
  'AbortError',
];

const DENY_URLS = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-web-extension:\/\//i,
  /gtag\/js/i,
  /googletagmanager\.com/i,
  /fingerprintjs/i,
];

function beforeBreadcrumb(breadcrumb) {
  if (!breadcrumb) {
    return breadcrumb;
  }

  const category = breadcrumb.category || '';
  if (category === 'console' || category.startsWith('console')) {
    return null;
  }

  if (breadcrumb.data && typeof breadcrumb.data === 'object') {
    const data = { ...breadcrumb.data };
    ['url', 'from', 'to'].forEach((key) => {
      if (typeof data[key] === 'string') {
        data[key] = scrubUrlPath(data[key]);
      }
    });
    delete data.request_body;
    delete data.body;
    delete data.response;
    breadcrumb.data = data;
  }

  return sentryBeforeSend({ breadcrumbs: [breadcrumb] })?.breadcrumbs?.[0] || breadcrumb;
}

export function initSentry() {
  if (!isSentryRuntimeEnabled()) {
    return false;
  }

  try {
    const environment = getSentryEnvironment();
    const release = getSentryRelease();

    Sentry.init({
      dsn: getSentryDsn(),
      environment,
      release: release || undefined,
      sendDefaultPii: false,
      tracesSampleRate: 0,
      profilesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      maxBreadcrumbs: 30,
      ignoreErrors: IGNORE_ERRORS,
      denyUrls: DENY_URLS,
      beforeSend: sentryBeforeSend,
      beforeBreadcrumb,
      integrations: (integrations) => {
        const withoutDefaults = integrations.filter((integration) => {
          const name = integration?.name || '';
          return !['BrowserTracing', 'BrowserProfiling', 'Replay', 'ReplayCanvas', 'Breadcrumbs'].includes(name);
        });

        withoutDefaults.push(Sentry.breadcrumbsIntegration({
          console: false,
          history: true,
          fetch: true,
          xhr: true,
          dom: true,
          sentry: true,
        }));

        return withoutDefaults;
      },
    });

    Sentry.setTag('application', SENTRY_APPLICATION);
    const companyName = (process.env.REACT_APP_COMPANY_NAME || '').replace(/"/g, '').trim();
    if (companyName) {
      Sentry.setTag('company', companyName);
    }

    if ((environment === 'staging' || environment === 'qa') && typeof window !== 'undefined') {
      window.__sentryTest = () => {
        throw new Error('Sentry staging validation');
      };
    }

    return true;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[Sentry] Init skipped after failure', error);
    }
    return false;
  }
}
